// src/App.jsx

import { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import { supabase } from "./lib/supabaseClient";
import ConsentBanner from './components/ConsentBanner';
import LegalConsentBanner from './components/LegalConsentBanner';
import Toast from './components/Toast';
import { usePageTracking } from './hooks/useAnalytics';

import Competitions from "./pages/Competitions";
import MyCompetitions from "./pages/MyCompetitions";
import SelectTeam from "./pages/SelectTeam";
import CompetitionView from "./pages/CompetitionView";
import Pontoon from "./pages/Pontoon";
import AdminFixtures from "./pages/AdminFixtures";
import KnockoutLeaderboard from "./pages/KnockoutLeaderboard";
import SeasonLeaderboard from "./pages/SeasonLeaderboard";
import ResetPassword from "./pages/ResetPassword";
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ProfileForm from "./components/ProfileForm";
import ProfileCheck from "./components/ProfileCheck";
import UserProfile from "./pages/UserProfile";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Dashboard from "./pages/Dashboard";
import WorldStage from "./pages/WorldStage";
import TrophyRoom from "./pages/TrophyRoom";
import Fixtures from "./pages/Fixtures";
import Support from "./pages/Support";
import MyTickets from "./pages/MyTickets";
import CompetitionJoinLanding from "./pages/CompetitionJoinLanding";
import ReferralLanding from "./pages/ReferralLanding";
import Squad from "./pages/Squad";
import SquadDetail from "./pages/SquadDetail";
import FriendLanding from "./pages/FriendLanding";
import FriendProfile from "./pages/FriendProfile";

import NotificationProvider from "./components/NotificationProvider";

import "./index.css";
import './ui';

// Wrapper component to enable page tracking inside Router context
function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  const [session, setSession] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [username, setUsername] = useState("");
  const [loadingSession, setLoadingSession] = useState(true);
  const [userId, setUserId] = useState(null);

  const isPasswordRecoveryFlow = () => {
    if (typeof window === "undefined") return false;
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    return hash.includes("type=recovery") || search.includes("type=recovery");
  };

  useEffect(() => {
    const getSessionAndRole = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setSession(session);
      const uid = session?.user?.id;
      setUserId(uid);

      if (uid) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("role, username")
          .eq("id", uid)
          .single();
        setUserRole(profile?.role);
        setUsername(profile?.username || "");
      }

      setLoadingSession(false);
    };

    getSessionAndRole();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        const uid = session?.user?.id;
        setUserId(uid);

        if (uid) {
          supabase
            .from("profiles")
            .select("role, username")
            .eq("id", uid)
            .single()
            .then(({ data }) => {
              setUserRole(data?.role);
              setUsername(data?.username || "");
            });
        } else {
          setUserRole(null);
          setUsername("");
        }
      }
    );

    return () => authListener.subscription.unsubscribe();
  }, []);


  if (loadingSession) return null;

  return (
    <div data-fc-ui className="fc-application">
    <a href="#fc-main-content" className="fc-skip-link">Skip to content</a>
    <Router>
      <NotificationProvider userId={userId}>
        <PageTracker />
        <Routes>
        {/* Public Landing Pages */}
        <Route path="/join/:joinKey" element={<CompetitionJoinLanding />} />
        <Route path="/invite/:referralCode" element={<ReferralLanding />} />
        <Route path="/friend/:friendId" element={<FriendLanding />} />
        
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<Terms session={session} userRole={userRole} username={username} />} />
        <Route path="/privacy" element={<Privacy session={session} userRole={userRole} username={username} />} />

        {/* Protected Routes (each page wraps itself in Layout) */}
        <Route
          path="/"
          element={
            session ? (
              isPasswordRecoveryFlow() ? (
                <Navigate to="/reset-password" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <Landing />
            )
          }
        />
        <Route
          path="/competitions"
          element={
            session ? (
              <ProfileCheck session={session}>
                <Competitions
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/my-competitions"
          element={
            session ? (
              <ProfileCheck session={session}>
                <MyCompetitions
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/trophy-room"
          element={
            session ? (
              <ProfileCheck session={session}>
                <TrophyRoom
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/fixtures"
          element={
            session ? (
              <ProfileCheck session={session}>
                <Fixtures
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/scout"
          element={<Navigate to="/dashboard" replace />}
        />
        <Route
          path="/world-stage/:competitionId"
          element={
            session ? (
              <ProfileCheck session={session}>
                <WorldStage
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/select-team/:competitionId"
          element={
            session ? (
              <ProfileCheck session={session}>
                <SelectTeam
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/competition/:competitionId"
          element={
            session ? (
              <ProfileCheck session={session}>
                <CompetitionView
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/pontoon/:competitionId"
          element={
            session ? (
              <ProfileCheck session={session}>
                <Pontoon
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/fixtures"
          element={
            session ? (
              <AdminFixtures
                session={session}
                userRole={userRole}
                username={username}
              />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/leaderboard/:competitionId"
          element={
            session ? (
              <ProfileCheck session={session}>
                <KnockoutLeaderboard
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/season-leaderboard/:competitionId"
          element={
            session ? (
              <ProfileCheck session={session}>
                <SeasonLeaderboard
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route path="/setup-profile" element={<ProfileForm />} />
        <Route
          path="/profile"
          element={
            session ? (
              <UserProfile
                session={session}
                userRole={userRole}
                username={username}
              />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            session ? (
              <ProfileCheck session={session}>
                <Dashboard
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/support"
          element={
            session ? (
              <ProfileCheck session={session}>
                <Support
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/my-tickets"
          element={
            session ? (
              <ProfileCheck session={session}>
                <MyTickets
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/pricing"
          element={<Navigate to="/dashboard" replace />}
        />
        <Route
          path="/subscription/settings"
          element={<Navigate to="/dashboard" replace />}
        />
        <Route
          path="/subscription/success"
          element={<Navigate to="/dashboard" replace />}
        />
        <Route
          path="/squad"
          element={
            session ? (
              <ProfileCheck session={session}>
                <Squad
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/squad/:squadId"
          element={
            session ? (
              <ProfileCheck session={session}>
                <SquadDetail
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/friend-profile/:userId"
          element={
            session ? (
              <ProfileCheck session={session}>
                <FriendProfile
                  session={session}
                  userRole={userRole}
                  username={username}
                />
              </ProfileCheck>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
      <Toast />
      <ConsentBanner />
      <LegalConsentBanner session={session} />
      </NotificationProvider>
    </Router>
    </div>
  );
}

export default App;
